import { Text, TextStyles } from '@cinch-labs/design-system'

import {
  JargonHubPreviewStory,
  RelatedJargon,
} from '@cinch-nx/landing-feature-jargon'
import {
  Head,
  JsonLdScript,
  formatFaqPageSchema,
} from '@cinch-nx/landing-ui-shared'
import { SVG, Wrapper } from '@cinch-nx/shared-ui'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { Speech3D } from './assets'
import { SearchForm } from './components/search-form/search-form'
import styles from './feature-jargon-hub.module.css'
import { JargonHubFeatureProps } from './get-static-props'

type SearchType = (
  searchTerm: JargonHubPreviewStory[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setResults: React.Dispatch<React.SetStateAction<any[]>>,
) => Promise<unknown>

const search: SearchType = (filteredResults, setResults) =>
  new Promise((resolve, reject) => {
    setResults(filteredResults)

    return resolve(filteredResults)
  })

export const JargonHubFeature = ({ stories }: JargonHubFeatureProps) => {
  const router = useRouter()
  const query = router.query?.q?.toString()
  const [searchQuery, setSearchQuery] = useState<string>(query || '')
  const [filteredStories, setFilteredStories] = useState<
    JargonHubPreviewStory[]
  >([])

  const [isSearchPending, setSearchPending] = useState(false)
  const searchJargon = (q: string) => {
    setSearchQuery(q || '')
    if (!q) {
      return
    }
    q = q.toLowerCase()

    setSearchPending(true)

    const filtered = stories.filter(
      (item) =>
        item.content?.fullTerm.toLowerCase().includes(q) ||
        item.content?.keywords
          ?.split(',')
          .map((p) => p.toLowerCase())
          .includes(q) ||
        item.content?.question.toLowerCase().includes(q),
    )

    return new Promise<void>((resolve) => {
      search(filtered, setFilteredStories)
        .then(() => {
          setSearchPending(false)
          resolve()
        })
        .catch(() => {
          setSearchPending(false)
          resolve()
        })
    })
  }

  useEffect(() => {
    if (query) {
      searchJargon(query)
    }
  }, [query])

  const storiesContent = stories.map((story) => story.content)
  return (
    <article className={styles.page}>
      <JsonLdScript
        schema={formatFaqPageSchema(storiesContent)}
        tagId="JargonPage"
      />
      <Head
        fallbackTitle="Jargon Buster - cinch"
        fallbackDescription="Want to understand more about how your car works or any obscure industry terms? Our jargon buster is here to make them faff-free."
      />

      <Wrapper>
        <div className={styles['container']}>
          <div className={styles.title}>
            <Text element="h1" type={TextStyles.mainTitle} lineHeight="display">
              Jargon Buster
            </Text>
          </div>
          <SearchForm
            initialValues={{
              search: searchQuery,
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onSubmit={(values: any) => searchJargon(values.search)}
            isPending={isSearchPending}
          ></SearchForm>
          {!isSearchPending && filteredStories.length > 0 && (
            <>
              <div className={styles.title}>
                <Text
                  element="h2"
                  type={TextStyles.vehicleSubTitle}
                  lineHeight="display"
                >
                  Search Results
                </Text>
              </div>
              <div data-testid="SearchResults">
                <RelatedJargon items={filteredStories} />
              </div>
            </>
          )}
          {!isSearchPending && filteredStories.length === 0 && searchQuery && (
            <div
              className={styles.noResultsWrapper}
              data-testid="no-jargon-found"
            >
              <Text className={styles.noResultsTitle} element="h2">
                Sorry, we haven't found any articles that match, try a different
                word or phrase
              </Text>
              <SVG
                children={Speech3D}
                size={195}
                className={styles.noResultsImage}
              />
            </div>
          )}
        </div>
        {stories?.length > 0 && (
          <>
            <div className={styles.title} data-testid="latest-jargon">
              <Text
                element="h2"
                type={TextStyles.vehicleSubTitle}
                lineHeight="display"
              >
                Latest
              </Text>
            </div>
            <RelatedJargon items={stories} />
          </>
        )}
      </Wrapper>
    </article>
  )
}
